import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { seoArticles } from '@/mocks/seoArticles';
import NotFound from '@/pages/NotFound';

export default function GuidePage() {
  const { slug } = useParams<{ slug: string }>();

  // Scroll to top when article loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = seoArticles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <article className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            {/* Header - About module style */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-12">
              <div className="lg:w-1/3">
                <span className="text-xs font-medium text-foreground-500 tracking-widest uppercase">
                  / Guide
                </span>
              </div>
              <div className="lg:w-2/3">
                <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 font-light leading-[1.2]">
                  {article.title}
                </h1>
                <p className="mt-4 text-sm text-foreground-500">{article.date} · itaobuy sheet editorial team</p>
              </div>
            </div>

            {/* Content Body - 40/60 split */}
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              {/* Left - Image */}
              <div className="lg:w-2/5">
                <div className="rounded-3xl overflow-hidden bg-secondary-100 aspect-[4/5] relative lg:sticky lg:top-24">
                  <img
                    src={article.articleImageUrl}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Right - Article Text */}
              <div className="lg:w-3/5">
                <span className="text-xs font-medium text-foreground-500 tracking-wider mb-6 block">
                  ( SEO Resource )
                </span>

                {/* Back link at top */}
                <div className="mb-8">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-2 border-primary-300 text-primary-700 rounded-full hover:bg-primary-50 smooth-transition whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to itaobuy.run Home
                  </Link>
                </div>

                {/* Article content */}
                <div className="space-y-6">
                  {article.content.map((block, i) => {
                    if (block.type === 'heading') {
                      return (
                        <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4 leading-snug">
                          {block.text}
                        </h2>
                      );
                    }
                    return (
                      <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                        {block.text}
                      </p>
                    );
                  })}
                </div>

                {/* Internal links throughout */}
                <div className="mt-8">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-2 border-primary-300 text-primary-700 rounded-full hover:bg-primary-50 smooth-transition whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to itaobuy.run Home
                  </Link>
                </div>

                <div className="mt-6">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-2 border-primary-300 text-primary-700 rounded-full hover:bg-primary-50 smooth-transition whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Explore the ItaobuySpreadsheet System
                  </Link>
                </div>

                {/* Other articles */}
                <div className="mt-10 pt-8 border-t border-background-200">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4">More SEO Guides</h3>
                  <div className="space-y-3">
                    {seoArticles
                      .filter((a) => a.slug !== slug)
                      .slice(0, 5)
                      .map((a) => (
                        <Link
                          key={a.slug}
                          to={`/guide/${a.slug}`}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-background-100 smooth-transition cursor-pointer"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                          <span className="text-sm text-foreground-700 hover:text-foreground-950 font-medium line-clamp-1">
                            {a.title}
                          </span>
                        </Link>
                      ))}
                  </div>
                </div>

                {/* Navigation arrows */}
                <div className="flex items-center gap-3 mt-8">
                  <Link
                    to="/"
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-foreground-300 text-foreground-500 hover:bg-background-100 smooth-transition cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                  </Link>
                  <span className="text-xs text-foreground-500">Back to all guides</span>
                </div>
              </div>
            </div>

            {/* Article Schema */}
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Article",
                  "headline": article.title,
                  "description": article.excerpt,
                  "image": article.articleImageUrl,
                  "datePublished": article.date,
                  "dateModified": article.date,
                  "author": { "@type": "Organization", "name": "itaobuy sheet editorial team" },
                  "publisher": { "@type": "Organization", "name": "itaobuy.run", "logo": { "@type": "ImageObject", "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg" } },
                  "url": `https://itaobuy.run/guide/${article.slug}`,
                  "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": `https://itaobuy.run/guide/${article.slug}`
                  }
                })
              }}
            />
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}